import React, { useState, useEffect, useMemo } from 'react';
import {
  Flex,
  Center,
  Box,
  Text,
  Image,
  Icon,
  Spinner,
  Button,
  useDisclosure,
} from '@chakra-ui/react';
import { CheckCircleIcon, EmailIcon } from '@chakra-ui/icons';
import CustomerApi from 'api/customer';
import moment from 'moment';
import Card from 'components/card/Card.js';
import AadhaarVerificationModal from '../AadhaarVerificationModal';
import PanVerificationModal from '../PanVerificationModal';
import BankAccounts from '../customerDetails/components/bankAccounts/index';
import { usePermissions } from 'contexts/PermissionContext';
import { Modules } from 'config/constant';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';
import { isValidUUID } from 'utils/fetchUtils';
import PageError from 'components/nopermission/PageError';

const CustomerDetails = (props) => {
  const { customerData, setCustomerData } = props;
  const [customerPortfolio, setCustomerPortfolio] = useState();
  const [loading, setLoading] = useState(false);
  const { getPermissionsForModule } = usePermissions();
  const [permissions, setPermissions] = useState(null);
  const [customerPermissions, setCustomerPermissions] = useState({});
  const [fetchCustomerDataError, setFetchCustomerDataError] = useState(false);
  const {
    isOpen: isAadhaarOpen,
    onOpen: onAadhaarOpen,
    onClose: onAadhaarClose,
  } = useDisclosure();
  const {
    isOpen: isPanOpen,
    onOpen: onPanOpen,
    onClose: onPanClose,
  } = useDisclosure();
  const [aadhaarVerified, setAadhaarVerified] = useState(false);
  const [panVerified, setPanVerified] = useState(false);

  const customerId = useMemo(() => IdFromUrl(window.location.href), []);
  function IdFromUrl(url) {
    const parts = url.split('/');
    const id = parts[parts.length - 1];
    return id;
  }

  console.log('props received', props, permissions);

  const FetchCustomerData = async () => {
    if (!isValidUUID(customerId)) {
      setFetchCustomerDataError(true);
      return;
    }
    try {
      setLoading(true);
      let response = await CustomerApi.getById(customerId);
      response = response.data;
      setLoading(false);
      if (response.status_code === 200) {
        console.log('Received from API', response);
        setCustomerData(response?.data);
      } else {
        setCustomerData({});
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  const FetchCustomerPortfolio = async () => {
    if (!isValidUUID(customerId)) {
      setFetchCustomerDataError(true);
      return;
    }
    try {
      setLoading(true);
      let response = await CustomerApi.getPortfolio(customerId);
      response = response.data;
      setLoading(false);
      if (response.status_code === 200) {
        console.log('Received Portfolio- ', response);
        setCustomerPortfolio(response?.data);
      } else {
        setCustomerPortfolio({});
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    const permissions = getPermissionsForModule([Modules.Kyc]);
    const customerpermissions = getPermissionsForModule([
      Modules.Customer,
      Modules.MakerCheckers,
    ]);
    setCustomerPermissions(customerpermissions);
    setPermissions(permissions);
  }, []);

  useEffect(() => {
    if (customerPermissions?.read) {
      FetchCustomerData();
      FetchCustomerPortfolio();
    }
  }, [customerPermissions]);

  const handleAadhaarVerify = (isverified) => {
    if (isverified) {
      setAadhaarVerified(true);
    }
    setCustomerData((prevData) => ({
      ...prevData,
      user_document: {
        ...prevData.user_document,
        aadhar_verified: true,
      },
    }));
  };

  const handlePanVerify = () => {
    setPanVerified(true);
  };

  const customerInfo = customerData?.customer || {};
  const customerAddress = customerData?.address;
  const customerDocument = customerData?.user_document;

  if (!customerPermissions?.read) {
    return <NoPermissionsMessage module={Modules.Customer} />;
  }

  return (
    <Card
      mt={4}
      bg='gray.200'>
      {fetchCustomerDataError ? (
        <PageError />
      ) : (
        <>
          {loading ? (
            <Flex
              justifyContent='center'
              alignItems='center'
              height='200px'>
              <Spinner size='xl' />
            </Flex>
          ) : customerInfo ? (
            <Flex
              direction='column'
              gap={8}>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                flex={1}
                gap={4}>
                <Card
                  p={6}
                  bg='white'>
                  <Flex
                    direction={{ base: 'column', md: 'row' }}
                    width='100%'
                    // bgColor='yellow'
                    gap={4}
                    justifyContent='space-between'>
                    <Flex
                      direction={{ base: 'column', md: 'row' }}
                      width='100%'
                      gap={8}>
                      <Center minW='150px'>
                        <Image
                          borderRadius='full'
                          boxSize='150px'
                          src={
                            customerData?.user_document?.profile_image
                              ? `data:image/jpeg;base64,${customerData?.user_document?.profile_image}`
                              : 'https://placehold.co/150x150?text=Profile+Picture'
                          }
                          alt='Profile Picture'
                        />
                      </Center>

                      <Flex direction='column'>
                        <Text
                          fontWeight='bold'
                          fontSize='xl'>
                          {customerInfo.name}
                        </Text>
                        {customerInfo.email && (
                          <Flex
                            gap={2}
                            align='center'>
                            <EmailIcon />
                            <Text>{customerInfo.email}</Text>
                            {customerInfo.email_verified && (
                              <CheckCircleIcon color='green' />
                            )}
                          </Flex>
                        )}
                        <Flex align='center'>
                          <Text>
                            {customerInfo.country_code} -{' '}
                            {customerInfo.phone_number}
                          </Text>
                        </Flex>
                        <Text>Status: {customerInfo.status}</Text>
                        <Text>Join Date: {moment(customerInfo.created_at).format('DD MMM YY, HH:mm')} </Text>
                      </Flex>
                    </Flex>
                    <Flex
                      display={{ base: 'none', md: 'flex' }}
                      width='100%'></Flex>
                    <Flex
                      direction='column'
                      width='100%'
                      gap={4}>
                      <Text fontWeight='medium'>
                        Idenitification Code {customerInfo.code}
                      </Text>
                      <Text fontWeight='medium'>
                        Language Code: {customerInfo.language_code}
                      </Text>
                      <Text fontWeight='medium'>
                        Password Enabled:{' '}
                        {customerInfo.password_enabled ? 'Yes' : 'No'}
                      </Text>
                      <Text fontWeight='medium'>
                        PIN Enabled: {customerInfo.pin_enabled ? 'Yes' : 'No'}
                      </Text>
                    </Flex>
                  </Flex>
                </Card>
              </Flex>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={8}>
                <Card
                  p={6}
                  bg='white'
                  width='100%'>
                  {customerPortfolio?.gold_locker?.gold_weight === 0 ? (
                    <Center h='100%'>
                      <Text
                        fontWeight='bold'
                        fontSize='lg'>
                        No Holding in Advance Book
                      </Text>
                    </Center>
                  ) : (
                    <>
                      <Text
                        fontWeight='bold'
                        fontSize='lg'
                        mb={4}>
                        Advance Book
                      </Text>
                      <Flex gap={2}>
                        <Text>Gold Weight:</Text>
                        <Text color='gray.500'>
                          {customerPortfolio?.gold_locker?.gold_weight} grams
                        </Text>
                      </Flex>
                      <Flex gap={2}>
                        <Text>Current Value:</Text>
                        <Text color='gray.500'>
                          {customerPortfolio?.gold_locker?.current_value} INR
                        </Text>
                      </Flex>
                    </>
                  )}
                </Card>
                <Card
                  p={6}
                  bg='white'
                  width='100%'>
                  {customerPortfolio?.saving_plan?.active_plan === 0 ? (
                    <Center h='100%'>
                      <Text
                        fontWeight='bold'
                        fontSize='lg'>
                        No Active Saving Plans
                      </Text>
                    </Center>
                  ) : (
                    <>
                      <Text
                        fontWeight='bold'
                        fontSize='lg'
                        mb={4}>
                        Saving Plans
                      </Text>
                      <Flex gap={2}>
                        <Text>Active Plans:</Text>
                        <Text color='gray.500'>
                          {customerPortfolio?.saving_plan?.active_plan}
                        </Text>
                      </Flex>
                      <Flex gap={2}>
                        <Text>Investment Value:</Text>
                        <Text color='gray.500'>
                          {customerPortfolio?.saving_plan?.investment_value} INR
                        </Text>
                      </Flex>
                    </>
                  )}
                </Card>
                <Card
                  p={6}
                  bg='white'
                  width='100%'>
                  <Text
                    fontWeight='bold'
                    fontSize='lg'
                    mb={4}>
                    KYC
                  </Text>
                  <Flex
                    direction='column'
                    gap={4}>
                    <Flex align='center'>
                      <Text>Aadhaar Number: {customerDocument?.aadhar}</Text>
                      {customerDocument?.aadhar_verified || aadhaarVerified ? (
                        <Icon
                          as={CheckCircleIcon}
                          color='green.500'
                          ml={2}
                        />
                      ) : (
                        <Button
                          ml={2}
                          onClick={onAadhaarOpen}
                          disabled={!permissions?.update || !permissions?.read}>
                          Verify
                        </Button>
                      )}
                    </Flex>
                    <Flex align='center'>
                      <Text>PAN Number: {customerDocument?.pan}</Text>
                      {customerDocument?.pan_verified || panVerified ? (
                        <Icon
                          as={CheckCircleIcon}
                          color='green.500'
                          ml={2}
                        />
                      ) : (
                        <Button
                          ml={2}
                          onClick={onPanOpen}
                          disabled={!permissions?.update || !permissions?.read}>
                          Verify
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                </Card>
              </Flex>

              <Card p={6}>
                <BankAccounts />
              </Card>

              {customerAddress &&
                (customerAddress.temporary || customerAddress.permanent) ? (
                <Flex direction={{ base: 'column', md: 'row' }}>
                  <Flex
                    direction={{ base: 'column', md: 'row' }}
                    gap={8}
                    width='100%'>
                    {customerAddress.temporary && (
                      <Card
                        p={6}
                        bg='white'
                        width='100%'>
                        <Text
                          fontWeight='bold'
                          fontSize='lg'
                          mb={4}>
                          Temporary Address
                        </Text>
                        <Text>
                          Address Line 1:{' '}
                          {customerAddress.temporary.address_line1 || 'N/A'}
                        </Text>
                        <Text>
                          Address Line 2:{' '}
                          {customerAddress.temporary.address_line2 || 'N/A'}
                        </Text>
                        <Text>
                          {' '}
                          City: {customerAddress.temporary.city || 'N/A'}
                        </Text>
                        <Text>
                          State: {customerAddress.temporary.state || 'N/A'}
                        </Text>
                        <Text>
                          {' '}
                          Pin: {customerAddress.temporary.pincode || 'N/A'}
                        </Text>
                        <Text>
                          Country: {customerAddress.temporary.country || 'N/A'}
                        </Text>
                      </Card>
                    )}
                    {customerAddress.permanent && (
                      <Card
                        bg='white'
                        width='100%'>
                        <Text
                          fontWeight='bold'
                          fontSize='lg'
                          mb={4}>
                          Permanent Address
                        </Text>
                        <Text>
                          Address Line 1:{' '}
                          {customerAddress.permanent.address_line1 || 'N/A'}
                        </Text>
                        <Text>
                          Address Line 2:{' '}
                          {customerAddress.permanent.address_line2 || 'N/A'}
                        </Text>
                        <Text>
                          City: {customerAddress.permanent.city || 'N/A'}
                        </Text>
                        <Text>
                          State: {customerAddress.permanent.state || 'N/A'}
                        </Text>
                        <Text>
                          Pin: {customerAddress.permanent.pincode || 'N/A'}
                        </Text>
                        <Text>
                          Country: {customerAddress.permanent.country || 'N/A'}
                        </Text>
                      </Card>
                    )}
                  </Flex>
                </Flex>
              ) : (
                <></>
              )}
            </Flex>
          ) : (
            <Text>No customer details available</Text>
          )}

          <br />

          {customerAddress && customerAddress.aadhar && (
            <Flex direction={{ base: 'column', md: 'row' }}>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={8}
                width='100%'>
                <Card
                  p={6}
                  bg='white'
                  width='100%'>
                  <Text
                    fontWeight='bold'
                    fontSize='lg'
                    mb={4}>
                    Aadhar Address
                  </Text>
                  <Text>
                    {customerInfo.name} {' ( '}
                    {customerAddress.aadhar.care_of}{' )'}
                  </Text>
                  <Text>
                    {' '}
                    House Number : {customerAddress.aadhar.house || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    Street : {customerAddress.aadhar.street || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    Location : {customerAddress.aadhar.loc || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    Village/Town/City : {customerAddress.aadhar.vtc || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    Post Office : {customerAddress.aadhar.po || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    Landmark : {customerAddress.aadhar.landmark || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    Sub-District : {customerAddress.aadhar.subdist || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    District : {customerAddress.aadhar.dist || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    State : {customerAddress.aadhar.state || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    Country : {customerAddress.aadhar.country || 'N/A'}
                  </Text>
                  <Text>
                    {' '}
                    Pincode : {customerAddress.aadhar.pincode || 'N/A'}
                  </Text>
                  <Text>
                    Date of Birth :{' '}
                    {customerAddress.aadhar.dob ? moment(customerAddress.aadhar.dob).format('DD MMM YYYY') : 'N/A'}
                  </Text>
                </Card>

              </Flex>
            </Flex>
          )}
          <AadhaarVerificationModal
            isOpen={isAadhaarOpen}
            onClose={onAadhaarClose}
            onVerify={handleAadhaarVerify}
            userId={customerId}
            func={FetchCustomerData}
          />
          <PanVerificationModal
            isOpen={isPanOpen}
            onClose={onPanClose}
            onVerify={handlePanVerify}
            userId={customerId}
            func={FetchCustomerData}
          />
        </>
      )}
    </Card>
  );
};

export default CustomerDetails;
